/**
 * CookieYes runtime yardımcıları (client).
 *
 * Kaynak sırası: önce CookieYes JS API (`getCkyConsent`, banner yüklendikten sonra
 * hazır), yoksa `cookieyes-consent` cookie'si.
 *
 * Cookie formatı (ham, encode edilmemiş):
 *   consentid:<id>,consent:yes|no,action:yes|no|<boş>,necessary:yes,functional:yes|no,
 *   analytics:yes|no,performance:yes|no,advertisement:yes|no
 *   - action boş → kullanıcı henüz karar vermedi (banner görünür)
 *   - action:yes → kullanıcı karar verdi (kabul / kısmi / ret)
 */
export const COOKIEYES_CONSENT_COOKIE = 'cookieyes-consent'

export const TRACKING_CATEGORIES = ['analytics', 'performance', 'advertisement'] as const

export const COOKIE_BANNER_SELECTOR = '.cky-consent-container, .cky-modal, [data-cky-tag]'

export interface CookieYesConsentSnapshot {
  isUserActionCompleted: boolean
  categories: Record<string, boolean>
}

export type GoogleConsentSignalState = 'granted' | 'denied'

export type GoogleConsentSignals = Record<
  'ad_storage' | 'ad_user_data' | 'ad_personalization' | 'analytics_storage' | 'functionality_storage' | 'personalization_storage' | 'security_storage',
  GoogleConsentSignalState
>

export const parseCookieYesConsentCookie = (cookieString: string): CookieYesConsentSnapshot | null => {
  const match = /(?:^|;\s*)cookieyes-consent=([^;]*)/.exec(cookieString || '')
  if (!match) return null

  let raw = match[1]
  try {
    raw = decodeURIComponent(raw)
  } catch {
    /* ham değerle devam */
  }

  let isUserActionCompleted = false
  const categories: Record<string, boolean> = {}

  raw.split(',').forEach((pair) => {
    const [key, value] = pair.split(':').map((part) => (part || '').trim())
    if (!key) return
    if (key === 'action') {
      isUserActionCompleted = value === 'yes'
      return
    }
    // consentid / consent meta alanlarıdır, kategori değildir
    if (key === 'consentid' || key === 'consent') return
    if (value === 'yes' || value === 'no') categories[key] = value === 'yes'
  })

  return { isUserActionCompleted, categories }
}

export const getCookieYesConsent = (): CookieYesConsentSnapshot | null => {
  if (typeof window === 'undefined') return null

  if (typeof window.getCkyConsent === 'function') {
    try {
      const result = window.getCkyConsent()
      if (result) {
        return { isUserActionCompleted: result.isUserActionCompleted === true, categories: result.categories || {} }
      }
    } catch {
      /* API hatası → cookie fallback */
    }
  }

  return parseCookieYesConsentCookie(document.cookie)
}

export const isCookieYesLoaded = (): boolean => typeof window !== 'undefined' && typeof window.getCkyConsent === 'function'

export const isCookieYesConsentCompleted = (): boolean => getCookieYesConsent()?.isUserActionCompleted === true

/** En az bir tracking kategorisi (analytics/performance/advertisement) kabul edilmiş mi? */
export const hasTrackingConsent = (): boolean => {
  const consent = getCookieYesConsent()
  if (!consent?.isUserActionCompleted) return false
  return TRACKING_CATEGORIES.some((category) => consent.categories[category] === true)
}

/** Kullanıcı karar vermiş ve hiçbir tracking kategorisini kabul etmemiş mi? */
export const hasUserRejectedAllTracking = (): boolean => {
  const consent = getCookieYesConsent()
  if (!consent?.isUserActionCompleted) return false
  return TRACKING_CATEGORIES.every((category) => consent.categories[category] !== true)
}

export const isCookieBannerElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  return !!target.closest(COOKIE_BANNER_SELECTOR)
}

/**
 * CookieYes notice banner'ı DOM'da ve görünür mü?
 * CookieYes, karar vermiş kullanıcıda container'a (`.cky-consent-container`), banner'ı kapatırken
 * ise notice'e (`[data-cky-tag="notice"]`) `cky-hide` (display:none) ekler.
 */
export const isCookieYesBannerVisible = (): boolean => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false
  const container = document.querySelector<HTMLElement>('.cky-consent-container')
  if (!container || container.classList.contains('cky-hide')) return false

  const notice = container.querySelector<HTMLElement>('[data-cky-tag="notice"]')
  if (notice?.classList.contains('cky-hide')) return false

  const isHiddenByStyle = (element: HTMLElement): boolean => {
    const style = window.getComputedStyle(element)
    return style.display === 'none' || style.visibility === 'hidden'
  }

  return !isHiddenByStyle(container) && !(notice && isHiddenByStyle(notice))
}

/** Tümünü kabul et: önce CookieYes API, yoksa banner'daki kabul butonu. */
export const acceptAllViaCookieYes = (): boolean => {
  if (typeof window === 'undefined') return false

  if (typeof window.performBannerAction === 'function') {
    window.performBannerAction('accept_all')
    return true
  }

  const acceptButton = document.querySelector<HTMLButtonElement>('.cky-btn-accept, [data-cky-tag="accept-button"]')
  if (acceptButton) {
    acceptButton.click()
    return true
  }

  return false
}

/**
 * Consent komutları dataLayer'a `Arguments` nesnesi olarak yazılmalı; düz dizi push
 * edilirse GTM bunu komut olarak işlemez. `gtag` shim'i yoksa aynı etkiyi veren fallback kullanılır.
 */
export const pushGoogleConsentUpdate = (signals: Partial<GoogleConsentSignals>): boolean => {
  if (typeof window === 'undefined') return false

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', signals)
    return true
  }

  window.dataLayer = window.dataLayer || []
  const dataLayer = window.dataLayer as unknown[]
  const pushAsArguments = function () {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  } as (...args: unknown[]) => void
  pushAsArguments('consent', 'update', signals)
  return true
}

export const GRANTED_TRACKING_SIGNALS: Partial<GoogleConsentSignals> = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
}

export const DENIED_TRACKING_SIGNALS: Partial<GoogleConsentSignals> = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
}

export const grantTrackingConsent = (): boolean => pushGoogleConsentUpdate(GRANTED_TRACKING_SIGNALS)

export const revokeTrackingConsent = (): boolean => pushGoogleConsentUpdate(DENIED_TRACKING_SIGNALS)
